import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Descriptions,
    Drawer,
    Input,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import AdminUsersAPI from '../api/adminUsers';

const { Paragraph, Text } = Typography;

const EVENT_COLOR = {
    delegate_reset: 'orange',
    admin_reset: 'red',
    delegate_grant: 'green',
    delegate_revoke: 'default',
    verify_fail: 'volcano',
    disable: 'default',
    activate: 'green',
};

function formatDate(s) {
    if (!s) return '—';
    try {
        return new Date(s).toLocaleString();
    } catch (e) {
        return s;
    }
}

const fullName = (first, last) => [first, last].filter(Boolean).join(' ') || '—';

function RoleTags({ row }) {
    return <>
        {row.is_admin && <Tag color="red">Administrator</Tag>}
        {row.is_delegate && <Tag color="blue">Helpdesk</Tag>}
        {row.disable && <Tag>Disabled</Tag>}
    </>;
}

function UserDetail({ username, onClose }) {
    const navigate = useNavigate();
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!username) return;
        setInfo(null);
        setError(null);
        AdminUsersAPI.getInfo(
            username,
            (res) => setInfo(res),
            (err) => {
                console.log(err);
                setError(err?.detail || 'Failed to load');
            }
        );
    }, [username]);

    const auditColumns = [
        { title: 'When', dataIndex: 'created_at', render: formatDate, width: 170 },
        {
            title: 'Event',
            dataIndex: 'event',
            render: (v) => <Tag color={EVENT_COLOR[v]}>{v}</Tag>,
        },
        { title: 'Actor', dataIndex: 'actor_username', render: (v) => v || '—' },
        { title: 'Note', dataIndex: 'note', render: (v) => v || '—' },
        { title: 'IP', dataIndex: 'ip', render: (v) => v || '—' },
    ];

    return <Drawer
        open={!!username}
        onClose={onClose}
        width={720}
        title={username}
        extra={info && <Button
            danger
            onClick={() => navigate(`/reset-2fa?username=${encodeURIComponent(info.username)}`)}
        >
            Reset 2FA…
        </Button>}
    >
        {error && <Alert type="error" showIcon message={error} />}
        {!info && !error && <Spin />}
        {info && <Space direction="vertical" size={24} style={{ width: '100%' }}>
            <Descriptions bordered size="small" column={1} title="Profile">
                <Descriptions.Item label="Username">
                    <Space wrap>
                        <Text strong copyable>{info.username}</Text>
                        <RoleTags row={info} />
                    </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Name">
                    {fullName(info.firstname, info.lastname)}
                </Descriptions.Item>
                <Descriptions.Item label="Name (TH)">
                    {fullName(info.firstnameTH, info.lastnameTH)}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                    {info.email ? <Text copyable>{info.email}</Text> : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Phone">{info.phone_no || '—'}</Descriptions.Item>
                <Descriptions.Item label="OU ID">{info.ouid || '—'}</Descriptions.Item>
                <Descriptions.Item label="Roles">{info.roles || '—'}</Descriptions.Item>
                <Descriptions.Item label="sys_roles">{info.sys_roles}</Descriptions.Item>
                <Descriptions.Item label="Created">{formatDate(info.create_time)}</Descriptions.Item>
                <Descriptions.Item label="Updated">{formatDate(info.update_time)}</Descriptions.Item>
            </Descriptions>

            <Descriptions bordered size="small" column={1} title="Security">
                <Descriptions.Item label="2FA">
                    {info.totp_enrolled
                        ? <Tag color="green">Enrolled</Tag>
                        : <Tag>Not enrolled</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Backup codes left">
                    {info.backup_codes_remaining}
                </Descriptions.Item>
                <Descriptions.Item label="Active tickets">{info.active_tickets}</Descriptions.Item>
            </Descriptions>

            <div>
                <Typography.Title level={5}>serviceValidation payload</Typography.Title>
                <Paragraph type="secondary">
                    The JSON a client app receives for this user from
                    /serviceValidation and /qrValidation.
                </Paragraph>
                {info.service_profile_error
                    ? <Alert
                        type="error"
                        showIcon
                        message="The payload cannot be built; validation would fail with a server error."
                        description={<Text code>{info.service_profile_error}</Text>}
                    />
                    : <Paragraph
                        copyable={{ text: JSON.stringify(info.service_profile, null, 2) }}
                        style={{ marginBottom: 0 }}
                    >
                        <pre style={{ maxHeight: 400, overflow: 'auto', margin: 0 }}>
                            {JSON.stringify(info.service_profile, null, 2)}
                        </pre>
                    </Paragraph>}
            </div>

            <div>
                <Typography.Title level={5}>Recent 2FA events</Typography.Title>
                <Table
                    rowKey="id"
                    size="small"
                    columns={auditColumns}
                    dataSource={info.recent_2fa_events || []}
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: 'No 2FA activity' }}
                />
            </div>
        </Space>}
    </Drawer>;
}

/** Admin-only user directory: search, then open a user for details. */
export default function AdminUsers() {
    const [messageApi, contextHolder] = message.useMessage();
    const [rows, setRows] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);

    const onSearch = (value) => {
        const q = (value || '').trim();
        if (q.length < 2) {
            messageApi.warning('Enter at least 2 characters.');
            return;
        }
        setLoading(true);
        AdminUsersAPI.search(
            q,
            (res) => {
                setRows(res || []);
                setLoading(false);
            },
            (err) => {
                console.log(err);
                setLoading(false);
                messageApi.error(err?.detail || 'Search failed');
            }
        );
    };

    const columns = [
        {
            title: 'Username',
            dataIndex: 'username',
            render: (v, row) => <Space wrap>
                <Text strong>{v}</Text>
                <RoleTags row={row} />
            </Space>,
        },
        {
            title: 'Name',
            render: (_, row) => fullName(row.firstname, row.lastname),
        },
        {
            title: 'Name (TH)',
            render: (_, row) => fullName(row.firstnameTH, row.lastnameTH),
        },
        { title: 'Email', dataIndex: 'email', render: (v) => v || '—' },
        { title: 'OU ID', dataIndex: 'ouid', render: (v) => v || '—' },
        { title: 'Roles', dataIndex: 'roles', render: (v) => v || '—' },
    ];

    return <Card title="Users">
        {contextHolder}
        <Paragraph type="secondary">
            Search by username, email, or name (English or Thai). Click a row
            for the full profile and 2FA state. Results are capped at 50.
        </Paragraph>
        <Input.Search
            placeholder="username, email or name"
            allowClear
            enterButton="Search"
            loading={loading}
            onSearch={onSearch}
            style={{ maxWidth: 480, marginBottom: 16 }}
        />
        {rows !== null && <Table
            rowKey="username"
            size="small"
            loading={loading}
            columns={columns}
            dataSource={rows}
            pagination={{ pageSize: 25, showSizeChanger: false, hideOnSinglePage: true }}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'No matching users' }}
            onRow={(row) => ({
                onClick: () => setSelected(row.username),
                style: { cursor: 'pointer' },
            })}
        />}
        <UserDetail username={selected} onClose={() => setSelected(null)} />
    </Card>;
}
